import React, { useState } from 'react';
import { contactInfo } from '../data/mock';
import { MapPin, Briefcase, CheckCircle2, AlertCircle, ArrowUpRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const fieldClass = "bg-white/60 backdrop-blur-lg border-[#8b5a2b]/20 focus:border-[#8b5a2b]/60 focus:ring-[#c9a962]/30 rounded-xl text-[#2a2420] placeholder:text-[#5a4a3a]/50";

const ContactSection = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: '',
    message: '',
    website_hp: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');

  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    let recaptchaToken = '';
    try {
      if (window.grecaptcha) {
        recaptchaToken = await window.grecaptcha.execute(
          process.env.REACT_APP_RECAPTCHA_SITE_KEY,
          { action: 'contact_form' }
        );
      }
    } catch (err) {
      console.warn('reCAPTCHA execution failed:', err);
    }

    try {
      const payload = { ...formData, 'g-recaptcha-response': recaptchaToken };
      const response = await axios.post(`${BACKEND_URL}/api/contact`, payload);
      setSubmitStatus('success');
      setStatusMessage(response.data.message || 'Your message has been sent successfully.');
      setFormData({ firstName: '', lastName: '', email: '', subject: '', message: '', website_hp: '' });

      setTimeout(() => {
        const section = document.getElementById('contact');
        if (section) window.scrollTo({ top: section.offsetTop - 50, behavior: 'smooth' });
      }, 100);
    } catch (error) {
      setSubmitStatus('error');
      setStatusMessage(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative z-10 max-w-6xl mx-auto px-6 py-20">
      <div className="text-center mb-14">
        <div className="text-xs uppercase tracking-[0.25em] text-[#8b5a2b] font-medium mb-4">Get in touch</div>
        <h2 className="text-4xl md:text-5xl font-semibold tracking-[-0.025em] text-[#2a2420]">
          Start a <span className="font-serif-display italic font-medium text-[#8b5a2b]">conversation.</span>
        </h2>
      </div>

      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
        {/* Form card */}
        <div className="relative p-8 md:p-10 rounded-3xl bg-white/60 backdrop-blur-xl border border-white/80 shadow-[0_8px_32px_-12px_rgba(80,50,20,0.12)] overflow-hidden">
          <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-gradient-to-br from-amber-200/40 to-rose-200/30 blur-2xl opacity-70" />
          <div className="relative">
            <h3 className="text-2xl font-semibold tracking-[-0.015em] text-[#2a2420] mb-1">Inquiries</h3>
            <p className="text-[#5a4a3a] text-sm mb-8">Questions, introductions, or commendations — please use the form below.</p>

            {submitStatus === 'success' ? (
              <div className="flex flex-col items-center justify-center text-center py-10 space-y-5">
                <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center text-green-600">
                  <CheckCircle2 className="w-7 h-7" />
                </div>
                <div>
                  <h4 className="text-2xl font-semibold tracking-[-0.015em] text-[#2a2420] mb-2">Message sent</h4>
                  <p className="text-[#5a4a3a] max-w-md">{statusMessage}</p>
                </div>
                <Button
                  onClick={() => setSubmitStatus(null)}
                  className="bg-[#2a2420] hover:bg-[#1a1410] text-[#fbf8f3] font-medium px-6 py-3 rounded-full"
                >
                  Send another
                </Button>
              </div>
            ) : (
              <>
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{statusMessage}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Honeypot */}
                  <div style={{ display: 'none' }} aria-hidden="true">
                    <Input type="text" name="website_hp" value={formData.website_hp} onChange={handleChange} tabIndex="-1" autoComplete="off" />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-[#5a4a3a] mb-1.5">First name</label>
                      <Input name="firstName" value={formData.firstName} onChange={handleChange} required className={fieldClass} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#5a4a3a] mb-1.5">Last name</label>
                      <Input name="lastName" value={formData.lastName} onChange={handleChange} required className={fieldClass} />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#5a4a3a] mb-1.5">Email</label>
                    <Input type="email" name="email" value={formData.email} onChange={handleChange} required className={fieldClass} />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#5a4a3a] mb-1.5">Subject</label>
                    <Input name="subject" value={formData.subject} onChange={handleChange} className={fieldClass} />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-[#5a4a3a] mb-1.5">Message</label>
                    <Textarea name="message" value={formData.message} onChange={handleChange} required rows={5} className={fieldClass} />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full group bg-[#2a2420] hover:bg-[#1a1410] text-[#fbf8f3] font-medium py-6 rounded-full transition-all hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2 mt-2"
                  >
                    {isSubmitting ? 'Sending…' : 'Send message'}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>

        {/* Sidebar cards */}
        <div className="space-y-5">
          {contactInfo.offices.map((office) => (
            <div key={office.title} className="relative p-7 rounded-3xl bg-white/60 backdrop-blur-xl border border-white/80 shadow-[0_8px_32px_-12px_rgba(80,50,20,0.12)]">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl bg-white/70 backdrop-blur-lg border border-white/90 flex items-center justify-center text-[#c9a962] flex-shrink-0">
                  <MapPin className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <h3 className="text-base font-semibold text-[#2a2420] pt-1">{office.title}</h3>
              </div>
              <div className="text-sm text-[#5a4a3a] space-y-0.5 pl-12">
                {office.address.map((line, i) => <p key={i}>{line}</p>)}
              </div>
            </div>
          ))}

          <div className="relative p-7 rounded-3xl bg-gradient-to-br from-[#3a2c20] to-[#2a2420] border border-[#8b5a2b]/30 shadow-[0_8px_32px_-12px_rgba(42,36,32,0.3)] overflow-hidden">
            <div className="absolute -top-20 -right-20 w-48 h-48 rounded-full bg-[#d4a056]/20 blur-2xl" />
            <div className="relative">
              <div className="flex items-start gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl bg-white/10 border border-white/20 flex items-center justify-center text-[#d4a056] flex-shrink-0">
                  <Briefcase className="w-4 h-4" strokeWidth={1.75} />
                </div>
                <h3 className="text-base font-semibold text-[#fbf8f3] pt-1">Employment</h3>
              </div>
              <p className="text-sm text-[#fbf8f3]/70 pl-12 leading-relaxed">
                To apply for a role at GSD, please submit your cover letter and CV using the contact form.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
